import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  EmissionPointsWithParametersView,
  EntryStatus,
  ParameterEntryCycleStatus,
  ParametersView,
  ParameterType,
  ShopsToolsEmissionPointsView,
  ShopsToolsView,
  SitesView,
} from '../../_generated';
import { DataEntryStore } from '../reducers/data-entry.reducer';

export const SUBMITTED_STATUSES = [EntryStatus.submitted, EntryStatus.submission_error];
export const APPROVED_STATUSES = [EntryStatus.approved, EntryStatus.submitted, EntryStatus.submission_error, ...SUBMITTED_STATUSES];
export const REVIEWED_STATUSES = [EntryStatus.in_review, EntryStatus.reviewed, EntryStatus.rejected, ...APPROVED_STATUSES];
export const COMPLETED_STATUSES = [EntryStatus.completed, ...REVIEWED_STATUSES];

export interface ChildrenStatus {
  total: number;
  completed: number;
  completedReviewed: number;
  completedApproved: number;
  completedSubmitted: number;
  processStep: string;
}

export interface ExtendedEmissionPointStatus {
  id: number;
  idTool: number;
  name: string;
  code: string;
  parameterType: EmissionPointsWithParametersView.ParameterTypeEnum;
  parameters: Array<ParametersView>;
  status: string;
  childrenStatus: ChildrenStatus;
}

export interface ExtendedToolStatus {
  id: number;
  code: string;
  name: string;
  emissionPoints: ExtendedEmissionPointStatus[];
  status: string;
  childrenStatus: ChildrenStatus;
}

export interface ExtendedShopStatus {
  id: number;
  code: string;
  name: string;
  tools: ExtendedToolStatus[];
  status: string;
  canSubmitForReview: boolean;
  showSubmitForReview: boolean;
  childrenStatus: ChildrenStatus;
}

export interface ExtendedSiteStatus {
  id: number;
  code: string;
  name: string;
  shops: ExtendedShopStatus[];
  status: string;
  canSubmitToCorpo: boolean;
  childrenStatus: ChildrenStatus;
  lastSubmissionDate: Date | null;
}

export const getDataEntryFeatureState = createFeatureSelector<DataEntryStore>('dataEntry');

export const getDataEntryState = createSelector(getDataEntryFeatureState, (state: DataEntryStore) => state);

export const selectFilteredData = createSelector(getDataEntryFeatureState, (state) => {
  const { dataType, sites, selectedSiteId, selectedShopId, selectedToolId, selectedEmissionPointId, selectedStatusFilter, selectedParameterIds } =
    state;

  const mapEmissionPoint = (emissionPoint: EmissionPointsWithParametersView, emissionPointsStatuses: ExtendedEmissionPointStatus[]): any => {
    const emissionPointStatus = emissionPointsStatuses.find((ep: ExtendedEmissionPointStatus) => ep.id === emissionPoint.id);

    // Get the selected parameter IDs for the current Parameter Type (dataType)
    const selectedParameterIdsArray = selectedParameterIds.get(dataType) ?? [];

    const mappedParameters = (emissionPoint.parameters ?? [])
      .filter((parameter: ParametersView) => {
        const statusMatch = selectedStatusFilter === '' || parameter.status === selectedStatusFilter;
        const parameterMatch = selectedParameterIdsArray.length === 0 || selectedParameterIdsArray.includes(parameter.id);
        return statusMatch && parameterMatch;
      })
      .map((parameter: ParametersView) => {
        return { ...parameter };
      });

    return {
      ...emissionPoint,
      parameters: mappedParameters,
      status: emissionPointStatus?.status,
      childrenStatus: emissionPointStatus?.childrenStatus,
    };
  };

  const mapTool = (tool: ShopsToolsEmissionPointsView, toolsStatuses: ExtendedToolStatus[]): any => {
    const toolStatus = toolsStatuses.find((t: ExtendedToolStatus) => t.id === tool.id);

    const emissionPoints = (tool.emissionPoints ?? [])
      .filter((ep: any) => selectedEmissionPointId === -1 || ep.id === selectedEmissionPointId)
      .map((ep: any) => mapEmissionPoint(ep, toolStatus?.emissionPoints ?? []))
      .filter((ep: any) => (ep.parameters?.length ?? 0) > 0);
    return {
      ...tool,
      emissionPoints,
      status: toolStatus?.status,
      childrenStatus: toolStatus?.childrenStatus,
    };
  };

  const mapShop = (shop: ShopsToolsView, shopsStatuses: ExtendedShopStatus[]): any => {
    const shopStatus = shopsStatuses.find((s: ExtendedShopStatus) => s.id === shop.id);

    const tools = (shop.tools ?? [])
      .filter((tool: any) => selectedToolId === -1 || tool.id === selectedToolId)
      .map((tool: any) => mapTool(tool, shopStatus?.tools ?? []))
      .filter((tool: any) => (tool.emissionPoints?.length ?? 0) > 0);

    return {
      ...shop,
      tools,
      status: shopStatus?.status,
      canSubmitForReview: shopStatus?.canSubmitForReview,
      showSubmitForReview: shopStatus?.showSubmitForReview,
      childrenStatus: shopStatus?.childrenStatus,
    };
  };

  const mapSite = (site: SitesView): any => {
    const allStatuses = calculateStatus(state);
    const siteStatus = allStatuses.find((status: any) => status.id === site.id);

    const shops = (site.shops ?? [])
      .filter((shop: any) => selectedShopId === -1 || shop.id === selectedShopId)
      .map((shop: any) => mapShop(shop, siteStatus?.shops ?? []))
      .filter((shop: any) => (shop.tools?.length ?? 0) > 0);

    return {
      ...site,
      status: siteStatus?.status,
      canSubmitToCorpo: siteStatus?.canSubmitToCorpo,
      shops,
      childrenStatus: siteStatus?.childrenStatus,
    };
  };

  return sites.data
    .filter((site: SitesView) => selectedSiteId === -1 || site.id === selectedSiteId)
    .map(mapSite)
    .filter((site: ExtendedSiteStatus) => (site.shops?.length ?? 0) > 0);
});

const inReviewProcess = [EntryStatus.rejected, EntryStatus.in_review, EntryStatus.reviewed, EntryStatus.approved];

const getChildrenDetailedStatus = (status: EntryStatus, childrenEntityArray: any[]) => {
  let processStep = 'data_entry';

  const completedSubmitted = childrenEntityArray.filter((p: any) => [EntryStatus.submitted].includes(p.status)).length;
  const completedApproved = childrenEntityArray.filter((p: any) =>
    [EntryStatus.approved, EntryStatus.submitted, EntryStatus.submission_error].includes(p.status),
  ).length;
  const completedReviewed = childrenEntityArray.filter((p: any) =>
    [EntryStatus.reviewed, EntryStatus.approved, EntryStatus.submitted, EntryStatus.submission_error].includes(p.status),
  ).length;
  const completed = childrenEntityArray.filter((p: any) => ![EntryStatus.in_progress, EntryStatus.rejected].includes(p.status)).length;

  if (inReviewProcess.includes(status)) {
    processStep = 'in_review';
  }

  return {
    total: childrenEntityArray.length,
    completed,
    completedReviewed,
    completedApproved,
    completedSubmitted,
    processStep,
  };
};

const getStatus = (childrenEntityArray: any[]) => {
  const hasAnyStatus = (status: EntryStatus) => childrenEntityArray.some((item) => item.status === status);

  const hasAllStatus = (status: EntryStatus) => childrenEntityArray.every((item) => item.status === status);

  const hasAllStatusInGroup = (statusGroup: EntryStatus[]) => childrenEntityArray.every((item) => statusGroup.includes(item.status));

  if (hasAnyStatus(EntryStatus.rejected)) {
    return EntryStatus.rejected;
  }

  if (hasAnyStatus(EntryStatus.submission_error)) {
    return EntryStatus.submission_error;
  }

  if (hasAllStatus(EntryStatus.completed) || (hasAnyStatus(EntryStatus.completed) && hasAllStatusInGroup(COMPLETED_STATUSES))) {
    return EntryStatus.completed;
  }

  if (hasAnyStatus(EntryStatus.in_review) && hasAllStatusInGroup(REVIEWED_STATUSES)) {
    return EntryStatus.in_review;
  }

  if (hasAllStatus(EntryStatus.reviewed) || (hasAnyStatus(EntryStatus.reviewed) && hasAllStatusInGroup(REVIEWED_STATUSES))) {
    return EntryStatus.reviewed;
  }

  if (hasAllStatus(EntryStatus.approved) || (hasAnyStatus(EntryStatus.approved) && hasAllStatusInGroup(APPROVED_STATUSES))) {
    return EntryStatus.approved;
  }

  if (hasAllStatus(EntryStatus.submitted)) {
    return EntryStatus.submitted;
  }

  return EntryStatus.in_progress;
};

const calculateStatus = (state: DataEntryStore) => {
  const { dataType, sites, selectedParameterIds } = state;

  // Get the selected parameter IDs for the current Parameter Type (dataType)
  const selectedParameterIdsArray = selectedParameterIds.get(dataType) ?? [];

  const mapEmissionPoint = (emissionPoint: EmissionPointsWithParametersView): any => {
    const mappedParameters = (emissionPoint.parameters ?? [])
      .filter((parameter: ParametersView) => {
        return selectedParameterIdsArray.length === 0 || selectedParameterIdsArray.includes(parameter.id);
      })
      .map((parameter: ParametersView) => {
        return { ...parameter };
      });

    const status = emissionPoint.status;
    return {
      ...emissionPoint,
      parameters: mappedParameters,
      status,
      childrenStatus: getChildrenDetailedStatus(status, mappedParameters),
    };
  };

  const mapTool = (tool: ShopsToolsEmissionPointsView): any => {
    const emissionPoints = (tool.emissionPoints ?? []).map((ep: any) => mapEmissionPoint(ep)).filter((ep: any) => (ep.parameters?.length ?? 0) > 0);

    const status = tool.status;

    let entityArray = emissionPoints;
    if (emissionPoints && emissionPoints.length > 0 && [ParameterType.residue, ParameterType.energy].includes(emissionPoints[0].parameterType)) {
      entityArray = emissionPoints[0].parameters;
    }

    return {
      ...tool,
      emissionPoints,
      status,
      childrenStatus: getChildrenDetailedStatus(status, entityArray),
    };
  };

  const mapShop = (shop: ShopsToolsView): any => {
    const tools = (shop.tools ?? []).map((tool: any) => mapTool(tool)).filter((tool: any) => (tool.emissionPoints?.length ?? 0) > 0);

    const status = shop.status;

    return {
      ...shop,
      tools,
      status,
      canSubmitForReview: !REVIEWED_STATUSES.includes(status), // Once submitted, can't submit again
      showSubmitForReview: true, // Just always show it for now?
      childrenStatus: getChildrenDetailedStatus(status, tools),
    };
  };

  const mapSite = (site: SitesView): ExtendedSiteStatus => {
    const shops = (site.shops ?? []).map((shop: any) => mapShop(shop)).filter((shop: any) => (shop.tools?.length ?? 0) > 0);

    const status = site.status;
    const canSubmitToCorpoStatus = [
      ParameterEntryCycleStatus.approved,
      ParameterEntryCycleStatus.submitted,
      ParameterEntryCycleStatus.submission_error,
    ];

    const requiredForSubmitToCorpoStatus = [ParameterEntryCycleStatus.approved, ParameterEntryCycleStatus.submission_error];

    // KEEP THIS FOR DEBUGGING
    // if (site.id === 2) {
    //   for (const shop of shops) {
    //     for (const tool of shop.tools) {
    //       for (const ep of tool.emissionPoints) {
    //         for (const param of ep.parameters) {
    //           console.log(
    //             `SITE SHOP TOOL EMISSION POINT PARAMETER: ${site.name} - ${shop.name} - ${tool.name} - ${ep.name} - ${param.name} - ${param.status}`
    //           );
    //         }
    //       }
    //     }
    //   }
    // }

    // We can submit a site to corpo if all entries are (approved or submitted or submission_error)
    // AND there should be at least one entry that is approved or submission_error
    const allEntriesApprovedOrSubmitted = shops.every((shop: ExtendedShopStatus) =>
      shop.tools.every((tool: ExtendedToolStatus) =>
        tool.emissionPoints.every((emissionPoint: ExtendedEmissionPointStatus) =>
          emissionPoint.parameters.every((parameter: ParametersView) => canSubmitToCorpoStatus.includes(parameter.status)),
        ),
      ),
    );

    const atLeastOneEntryApproved = shops.some((shop: ExtendedShopStatus) =>
      shop.tools.some((tool: ExtendedToolStatus) =>
        tool.emissionPoints.some((emissionPoint: ExtendedEmissionPointStatus) =>
          emissionPoint.parameters.some((parameter: ParametersView) => requiredForSubmitToCorpoStatus.includes(parameter.status)),
        ),
      ),
    );

    const canSubmitToCorpo = allEntriesApprovedOrSubmitted && atLeastOneEntryApproved;

    return {
      ...site,
      shops,
      status,
      canSubmitToCorpo: true, //canSubmitToCorpo,
      childrenStatus: getChildrenDetailedStatus(status, shops),
      lastSubmissionDate: site.lastSubmissionDate ? new Date(site.lastSubmissionDate) : null,
    };
  };

  return sites.data.map(mapSite).filter((site: ExtendedSiteStatus) => site.shops?.length > 0);
};
